// policyExt — the two policy domains being prototyped ahead of the
// OpenAPI contract: model governance and spend guardrails. The gen'd
// PolicyContent type is strict, so pages cast content to ExtendedContent
// to read/write these. When the Go side adopts them, these move into
// api/api/openapi.yaml and this file is deleted.
import type { components } from "@/lib/api/gen/schema";

export type ModelsPolicy = {
  /** allowlist = only listed models may be called; blocklist = listed models denied */
  mode?: "allowlist" | "blocklist";
  models?: string[];
  /** what enforcement does when a call violates the list */
  on_violation?: "block" | "downgrade" | "alert";
  /** target model when on_violation = downgrade */
  downgrade_to?: string;
};

export type SpendPolicy = {
  enable?: boolean;
  /** true = enforce on breach; false = alert only */
  remediate?: boolean;
  /** per-session ceiling, USD */
  session_usd?: number;
  /** per-user rolling daily ceiling, USD */
  user_daily_usd?: number;
  on_breach?: "block" | "downgrade" | "alert";
  downgrade_to?: string;
};

// AgentRemediate — the devel agent-hook remediate tri-state. Maps 1:1 onto the
// Go policy.ToolGuard/CommandGuard Remediate *string enum (api/internal/policy/
// model.go) and, at deploy, onto trex's bool|"approval" union (uc.go
// agentRemediate): observe→false, block→true, approval→"approval". Absent is
// treated as "observe" (fail-closed to non-blocking) by the Go validator.
export type AgentRemediate = "observe" | "block" | "approval";

// ToolGuard — per-coding-agent tool deny list (claude_sensor.tool_guard et
// al. in the BRU schema, Confluence BluerockUltraSecurityCustomerPolicy).
export type ToolGuard = {
  enable?: boolean;
  /** tri-state: observe = log only, block = hard-block, approval = ask the human */
  remediate?: AgentRemediate;
  denied?: string[];
};

// CommandGuard — Claude-only human-approval gate for dangerous shell commands
// (claude_sensor.command_guard). Same tri-state remediate as ToolGuard;
// approval re-introduces the permission prompt even under bypassPermissions.
export type CommandGuard = {
  enable?: boolean;
  remediate?: AgentRemediate;
  /** token-glob command patterns (e.g. "git push -f", "terraform destroy") */
  patterns?: string[];
};

// AgentSensor — one coding agent's hook block. command_guard + attribution are
// Claude-only in the devel trex schema; the Go validator rejects them on the
// gemini/codex/cursor sensors, and the deployer never writes them there.
export type AgentSensor = {
  tool_guard?: ToolGuard;
  command_guard?: CommandGuard;
  attribution?: { tag_tool_use_id?: boolean };
};

// BRU enforcement modes: observe (remediate=false), enforce-inline
// (remediate=true, inline=true → synchronous block), enforce-async
// (remediate=true, inline=false). `inline` defaults true.
export type WithInline = { inline?: boolean };

// KernelRule — one kernel_sensor sub-policy (host-wide LSM layer).
export type KernelRule = { enable?: boolean; remediate?: boolean; inline?: boolean };

export type ExtendedContent = components["schemas"]["PolicyContent"] & {
  models?: ModelsPolicy;
  spend?: SpendPolicy;
  claude_sensor?: AgentSensor;
  gemini_sensor?: AgentSensor;
  codex_sensor?: AgentSensor;
  cursor_sensor?: AgentSensor;
  /** host-wide kernel protections — lives OUTSIDE app scopes (BRU: top-level kernel_sensor) */
  kernel_sensor?: Record<string, KernelRule>;
  /** named per-app sandbox scopes (BRU app_policies; root content = "default") */
  app_scopes?: Record<string, ExtendedContent>;
};

export const AGENT_SENSORS = [
  ["claude_sensor", "Claude Code"],
  ["gemini_sensor", "Gemini CLI"],
  ["codex_sensor", "Codex CLI"],
  ["cursor_sensor", "Cursor"],
] as const;

// ── MCP custom message filters ───────────────────────────────────────────────
// Prototyped ahead of the OpenAPI contract (like models/spend above). Mirrors
// the PaaS McpMessageFilterEditor authoring model and the shape the self-hosted
// UC deployer already emits from allow/deny lists (uc.go buildMessageFilter):
// agentic_sensor.mcp.mcp_message_filter.message_filters[].{server_name,filters}.
// These custom filters are authored IN ADDITION to allow/deny lists; the
// deployer appends them onto the auto-derived per-server filters.
//
// BACKEND NOTE: saving a policy that contains `message_filter` requires the Go
// side (policy.MCPServer struct + validateMCP + buildMessageFilter) to adopt
// the field first — api/internal/policy/schema.go Validate() uses
// DisallowUnknownFields and 400s on anything not in the typed Content struct.
// Until openapi.yaml adopts it, Policy.tsx reads/writes servers via the
// ExtendedMcpServer cast below. See SELFHOSTED_TOOL_FILTER_DESIGN.md §4.

/** One `if_all` condition: a JSON-pointer property that must equal `value`. */
export type McpIfAllCondition = {
  /** JSON-pointer over the MCP frame, e.g. "/method", "/params/name". */
  property: string;
  value: string;
};

/** The `enforce` clause: allow/deny a set of values on a JSON-pointer property. */
export type McpEnforceClause = {
  /** JSON-pointer, e.g. "/params/name", "/params/arguments/cmd", "/result/tools/0/name". */
  property: string;
  values: string[];
  /** true = only listed values allowed (allowlist); false = listed values blocked (denylist). */
  allow_list: boolean;
};

/** One custom message filter: when all `if_all` match, apply `enforce`. */
export type McpMessageFilter = {
  /** Stable rule name — surfaces in violation events. */
  name: string;
  if_all: McpIfAllCondition[];
  enforce: McpEnforceClause;
};

// PolicyMcpServer + the not-yet-in-OpenAPI `message_filter` array. Cast
// `mcp.servers[name]` to this at the edit site until the generated schema
// gains the field (then this alias collapses to the generated type).
export type ExtendedMcpServer = components["schemas"]["PolicyMcpServer"] & {
  message_filter?: McpMessageFilter[];
};

/** `/method` values offered as a dropdown in the condition editor. */
export const MCP_METHODS = [
  "tools/call",
  "tools/list",
  "prompts/get",
  "prompts/list",
  "resources/read",
  "resources/list",
] as const;

/** Common enforce.property presets (free-form still allowed). */
export const MCP_ENFORCE_PRESETS = [
  "/params/name",
  "/params/arguments/cmd",
  "/result/tools/*/name",
] as const;

export type McpFilterTemplateId = "block-tools" | "allowlist-tools" | "block-arg-value";

export const MCP_FILTER_TEMPLATES: {
  id: McpFilterTemplateId;
  label: string;
  description: string;
  build: () => McpMessageFilter;
}[] = [
  {
    id: "block-tools",
    label: "Block tool calls",
    description: "Denylist on /params/name — add the tool names to block.",
    build: () => ({
      name: "block_tools",
      if_all: [{ property: "/method", value: "tools/call" }],
      enforce: { property: "/params/name", values: [], allow_list: false },
    }),
  },
  {
    id: "allowlist-tools",
    label: "Allowlist tool calls",
    description: "Allowlist on /params/name — only listed tools may be called; everything else blocks.",
    build: () => ({
      name: "allow_only_listed",
      if_all: [{ property: "/method", value: "tools/call" }],
      enforce: { property: "/params/name", values: [], allow_list: true },
    }),
  },
  {
    id: "block-arg-value",
    label: "Block argument value",
    description: "Denylist on a tool-argument value. Set the property path and the values to block.",
    build: () => ({
      name: "block_arg_value",
      if_all: [{ property: "/method", value: "tools/call" }],
      enforce: { property: "/params/arguments/cmd", values: [], allow_list: false },
    }),
  },
];
